import React, { ReactNode, useEffect } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { z } from "zod";
import { dismissNativeKeyboardOnDrag } from "../../../tools/native-keyboard";
import { usePushOpenTracking } from "../../../domains/notification/usePushOpenTracking";
import {
  DESKTOP_SIDEBAR_WIDTH,
  DesktopSidebar,
  type DesktopSidebarDestination,
  type DesktopSidebarState,
} from "../desktopSidebar/DesktopSidebar";
import { AppSidebarAudioPlayer } from "../desktopSidebar/AppSidebarAudioPlayer";
import MiniAudioPlayer from "../../media/MiniAudioPlayer";
import { DesktopRoomInfoHost } from "../../room/DesktopRoomInfoHost";
import { useNavBadges } from "../navbars/Navbar";
import { openDesktopQuickComposer } from "../../gpt/DesktopQuickComposer";
import {
  DesktopSearchPaletteHost,
  openDesktopSearchPalette,
} from "../../search/DesktopSearchPaletteHost";
import { createLocalStore, createMemoryStore } from "../../../lib/store";
import { useApiInfiniteQuery } from "../../../services/api";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import {
  assistantConversationListTitle,
  conversationListTitle,
} from "../../../domains/gpt/transcript";
import { useConversationEngine } from "../../../domains/assistant/useConversationEngine";
import { useNewChatGeneration } from "../../../domains/gpt/stores/newChatGenerationStore";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { ConversationMenu } from "../../gpt/ConversationMenu";
import {
  CONVERSATION_SHARE_FLAG,
  useShareConversation,
  useUnshareConversation,
} from "../../../domains/gpt/hooks/useShareConversation";
import {
  useDeleteConversation,
  useRenameConversation,
} from "../../../domains/gpt/hooks/useManageConversation";
import { liveActiveTurn } from "../../../../../api/src/shared/domain/gpt-turn";
import { useLogOutUser } from "../../../domains/user/hooks/useLogOutUser";
import {
  type CurrentUser,
  useCurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";

/**
 * Routes that take the whole screen, with no sidebar and no shell scroller.
 *
 * `/signup/onboarding` is the original: every step owns its own
 * `overflow-y-auto`, and letting the shell scroll too nests two scrollers.
 * `/onboarding/practice` also owns the full viewport and its list scroller.
 * `/announcements/preview` joins it because the admin visualizer frames it to
 * judge one sheet — a sidebar in the frame is chrome the reader being previewed
 * will never see.
 */
export function isChromelessRoute(pathname: string): boolean {
  return (
    pathname.startsWith("/signup/onboarding") ||
    matchPath(pathname, "/onboarding/practice") !== null ||
    pathname.startsWith("/announcements/preview")
  );
}
import { useDesktopAuthGateRequest } from "../../../domains/auth/hooks/useDesktopAuthGateRequest";
import {
  ACCOUNT_PAGE,
  openDesktopSettings,
} from "../../../domains/app/stores/desktopSettingsStore";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useChangeLanguage } from "../../../domains/app/hooks/useChangeLanguage";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { DesktopSettingsHost } from "../../../view/settings/desktop/DesktopSettingsModal";
import { matchesSegmentPrefix } from "../../../domains/auth/publicBrowserPaths";

/**
 * The sign-in and onboarding flow: the front door, the auth pages, the
 * invitation and OAuth landings, and the onboarding steps and fixups.
 */
const AUTH_FLOW_PATHS = [
  "/welcome",
  "/signup",
  "/login",
  "/invite",
  "/oauth",
  "/screenshot-auth",
  "/onboarding",
] as const;

export function isAuthFlowRoute(pathname: string): boolean {
  return AUTH_FLOW_PATHS.some((prefix) =>
    matchesSegmentPrefix(pathname, prefix)
  );
}

/**
 * The app's shortcuts: ⌘K the search palette, ⇧⌘, the settings, ⌘J a new
 * conversation. They belong to the app, never to the auth flow: a palette over
 * a signup step, or a shortcut that leaves it, breaks the flow. Exported for its
 * test.
 */
export function useDesktopShortcuts(
  isSignedIn: boolean,
  isAuthFlow: boolean
): void {
  const history = useHistory();
  useEffect(() => {
    if (isAuthFlow) return;
    function handleKeyDown(e: KeyboardEvent) {
      // A shortcut never opens a modal over another one, nor leaves the page
      // behind one: a palette or the settings over a welcome would be a second
      // modal, and
      // leaving the page under the code modal drops the code asked for there
      // (the request stands over that page only).
      if (document.querySelector('[aria-modal="true"]') !== null) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === "k") {
        e.preventDefault();
        e.stopPropagation();
        // The palette where one can open, the search page otherwise (a
        // visitor, whom the logged-out guard sends to the door).
        if (openDesktopSearchPalette(null)) return;
        history.push("/search");
      }
      // ⇧⌘, as on macOS apps. `code`, because Shift turns the key into "<"
      // on most layouts.
      if (isSignedIn && mod && e.shiftKey && e.code === "Comma") {
        e.preventDefault();
        e.stopPropagation();
        openDesktopSettings(ACCOUNT_PAGE);
        return;
      }
      if (mod && e.key === "j") {
        e.preventDefault();
        e.stopPropagation();
        // A page that floats the quick composer (Explore) gets it back rather
        // than being left. The one binding of ⌘J lives here, so no page
        // listener can fire alongside it.
        if (openDesktopQuickComposer()) return;
        history.push("/gpt/chat/new");
      }
    }
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  });
}

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * Whether the desktop sidebar is on screen: the desktop shell, a signed-in
 * member, off the onboarding takeover (`DesktopLayout`). A page under it
 * leaves the logo to the sidebar instead of repeating it in its own header.
 */
export function useHasDesktopSidebar(): boolean {
  const { currentUser } = useCurrentUser();
  const location = useLocation();
  return (
    isDesktopShell() &&
    currentUser !== null &&
    !isChromelessRoute(location.pathname)
  );
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  usePushOpenTracking();
  const { currentUser, isAuthenticated, isLoading } = useCurrentUser();
  const location = useLocation();

  // The signup/onboarding flow is a self-contained full-takeover: every step
  // (carousel slides + the profiling conversation) owns its own inner
  // `overflow-y-auto` scroller. Letting the shell ALSO scroll nests two
  // scrollers, and because the shell uses `h-screen` (100vh > the mobile visual
  // viewport when the URL bar shows) the outer one gains phantom scrollable
  // height → the intermittent double-scroll. So on onboarding the shell doesn't
  // scroll and tracks the dynamic viewport (`h-dvh`, the app's convention
  // elsewhere) — mirroring the desktop layout's `overflow-hidden`.
  const isOnboardingRoute = isChromelessRoute(location.pathname);

  // Desktop layout: sidebar + full-width content area
  if (isDesktopShell()) {
    return (
      <DesktopLayout
        currentUser={currentUser}
        hasFailedSession={isAuthenticated && !isLoading && currentUser === null}
      >
        {children}
      </DesktopLayout>
    );
  }

  // Mobile / native layout
  return (
    <div className="min-h-screen bg-ds-white">
      <div
        onTouchMove={dismissNativeKeyboardOnDrag}
        className={
          isOnboardingRoute
            ? "relative h-dvh w-full overflow-hidden"
            : "relative h-screen w-full overflow-y-auto"
        }
      >
        {children}
      </div>
    </div>
  );
};

function DesktopLayout({
  currentUser,
  hasFailedSession,
  children,
}: {
  currentUser: ReturnType<typeof useCurrentUser>["currentUser"];
  hasFailedSession: boolean;
  children: ReactNode;
}): React.JSX.Element {
  const location = useLocation();
  useDesktopShortcuts(currentUser !== null, isAuthFlowRoute(location.pathname));
  // The app behind the auth gate is a shell — every private query is refused a
  // token client-side, and the API 401s a request that arrives without one —
  // but it still took focus and clicks, so it LOOKED usable. `inert` takes the
  // whole subtree out of the focus order and the click path, which is also the
  // fix for Tab escaping the modal into the page behind it.
  //
  // NOT a security control, and it cannot be one: anything rendered can be
  // removed with devtools. The boundary is the API.
  const isAuthGateUp = useDesktopAuthGateRequest() !== null;
  // The signup/onboarding flow is a focused takeover — no sidebar, so the
  // post-onboarding profiling conversation can run full-width like JuisciGPT.
  const isOnboardingRoute = isChromelessRoute(location.pathname);
  return (
    <div
      // Presence is what the attribute means, so it is an empty string when on
      // and absent when off. React 18 has no boolean `inert` prop (that is 19).
      inert={isAuthGateUp ? "" : undefined}
      className="flex h-screen bg-ds-white"
    >
      {/* The sidebar is the signed-in member's: a visitor with no session
          gets none, on the auth pages and behind the auth gate alike. A
          session whose user failed to load keeps it, in its pending state:
          the destinations work without the user. While the user loads, the
          boot skeleton draws that pending sidebar itself. */}
      {/* Audio plays in the sidebar; where there is none, the floating player
          keeps the listener's controls on screen. */}
      {(() => {
        if (isOnboardingRoute) return <MiniAudioPlayer />;
        if (currentUser !== null)
          return <AppDesktopSidebar currentUser={currentUser} />;
        if (hasFailedSession) return <PendingDesktopSidebar />;
        return <MiniAudioPlayer />;
      })()}
      <div
        className="flex-1 overflow-hidden"
        style={
          {
            transform: "translateZ(0)",
            "--navbar-height": "0px",
          } as React.CSSProperties
        }
      >
        {children}
      </div>
      {isAuthGateUp ? null : <DesktopSearchPaletteHost />}
      {isAuthGateUp ? null : <DesktopSettingsHost />}
      {isAuthGateUp ? null : <DesktopRoomInfoHost />}
    </div>
  );
}

// Collapsed or not is a reading preference, so it outlives the session. The
// boot skeleton reads it too, so the sidebar it draws has the reader's shape.
export const useDesktopSidebarState = createLocalStore<DesktopSidebarState>(
  "desktop-sidebar-state",
  "expanded",
  z.enum(["expanded", "collapsed"])
);

// The dragged width, a reading preference too. Out of bounds (an older build,
// hand-edited storage) falls back to the default rather than being clamped.
export const useDesktopSidebarWidth = createLocalStore<number>(
  "desktop-sidebar-width",
  DESKTOP_SIDEBAR_WIDTH.default,
  z.number().int().min(DESKTOP_SIDEBAR_WIDTH.min).max(DESKTOP_SIDEBAR_WIDTH.max)
);

// Kept across navigations: opening a result must not clear the search. Owned
// by the user who typed it, so a later session in the same tab starts empty
// instead of sending the previous user's query on its own list.
const useDesktopSidebarSearch = createMemoryStore<{
  uid: string | null;
  text: string;
}>({ uid: null, text: "" });

export function activeDestinationOf(
  pathname: string
): DesktopSidebarDestination | null {
  // A room page is inside Rooms, room-scoped JuisciGPT included.
  if (pathname.startsWith("/rooms") || pathname.startsWith("/room/"))
    return "rooms";
  if (pathname.startsWith("/discovery")) return "explore";
  // Covers the library (`/visual-abstracts`) and one poster (`/visual-abstract/:id`).
  if (pathname.startsWith("/visual-abstract")) return "visual-abstracts";
  return null;
}

function activeConversationIdOf(pathname: string): string | null {
  const match = matchPath<{ id: string }>(pathname, {
    path: [
      "/gpt/chat/conversation/:id",
      "/room/gpt/chat/conversation/:roomId/:id",
    ],
  });
  return match === null ? null : match.params.id;
}

function PendingDesktopSidebar(): React.JSX.Element {
  const { pathname } = useLocation();
  const [state, setState] = useDesktopSidebarState();
  const [width, setWidth] = useDesktopSidebarWidth();
  return (
    <DesktopSidebar
      state={state}
      width={width}
      onWidthChange={setWidth}
      onToggle={() => {
        setState(state === "expanded" ? "collapsed" : "expanded");
      }}
      activeDestination={activeDestinationOf(pathname)}
      session={{ status: "pending" }}
      audioPlayer={
        <AppSidebarAudioPlayer
          sidebar={state}
          onExpandSidebar={() => {
            setState("expanded");
          }}
        />
      }
    />
  );
}

function AppDesktopSidebar({
  currentUser,
}: {
  currentUser: CurrentUser;
}): React.JSX.Element {
  const { pathname } = useLocation();
  const { activeLang } = useActiveLang();
  const changeLanguage = useChangeLanguage();
  const logOut = useLogOutUser();
  const badges = useNavBadges();
  const [state, setState] = useDesktopSidebarState();
  const [width, setWidth] = useDesktopSidebarWidth();
  const [, setNewChatGeneration] = useNewChatGeneration();
  const history = useHistory();
  const activeConversationId = activeConversationIdOf(pathname);
  // Sharing rides its own gate, like the conversation page's menu.
  const isShareEnabled = useFeatureIsOn(CONVERSATION_SHARE_FLAG);
  const shareConversation = useShareConversation();
  const unshareConversation = useUnshareConversation();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  // The same actions as the "…" of a conversation page, on each recent row
  // (revealed on hover). Deleting the conversation on screen leaves nothing
  // to read, so it lands on a new conversation.
  function conversationMenu(conversation: {
    id: string;
    storedTitle: string | null;
    questionCount: number;
    isShared: boolean;
  }): React.ReactNode {
    return (
      <ConversationMenu
        title={conversation.storedTitle}
        onShare={
          isShareEnabled
            ? () => {
                void shareConversation(
                  conversation.id,
                  conversation.questionCount
                );
              }
            : null
        }
        onUnshare={
          isShareEnabled && conversation.isShared
            ? () => {
                void unshareConversation(conversation.id);
              }
            : null
        }
        onDebug={null}
        onRename={(title) => renameConversation(conversation.id, title)}
        onDelete={async () => {
          const deleted = await deleteConversation(conversation.id);
          if (deleted === null) return false;
          // The legacy row of a continuation takes the open continuation with it.
          if (deleted.some((id) => id === activeConversationId))
            history.replace("/gpt/chat/new");
          return true;
        }}
        // The fade is gated on `(hover: hover)`, like the history drawer's rows:
        // where hover does not exist (a touchscreen), a hidden trigger would
        // still take the taps on the end of the row. It simply stays visible.
        className="transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-within:opacity-100 [@media(hover:hover)]:data-[open=true]:opacity-100"
      />
    );
  }
  const uid = currentUser.uid;
  const [storedSearch, setStoredSearch] = useDesktopSidebarSearch();
  const search = storedSearch.uid === uid ? storedSearch.text : "";
  const query = useDebouncedValue(search.trim(), 300);

  // The recents list the engine that answers new questions.
  const engine = useConversationEngine();
  const conversationsInput = {
    query:
      query === ""
        ? { limit: 20, sort: "newest" as const }
        : { limit: 20, q: query, sort: "newest" as const },
  };
  const conversationsQuery = useApiInfiniteQuery(
    "/webapp/private/conversations",
    engine === "legacy" ? conversationsInput : false
  );
  const assistantQuery = useApiInfiniteQuery(
    "/assistant/conversations",
    engine === "assistant" ? conversationsInput : false
  );

  // The query these rows answer, not the one being typed: the debounce keeps
  // the previous result on screen for a beat.
  const searched = query !== "";
  const recents = (() => {
    if (
      engine === "pending" ||
      conversationsQuery.state === "pending" ||
      assistantQuery.state === "pending"
    )
      return { status: "pending" as const };
    if (
      conversationsQuery.state === "error" ||
      assistantQuery.state === "error"
    )
      return { status: "error" as const };
    if (conversationsQuery.state === "success") {
      const now = Date.now();
      return {
        status: "success" as const,
        conversations: conversationsQuery.docs.map((conversation) => ({
          id: conversation.id,
          title: conversationListTitle(conversation, now),
          menu: conversationMenu({
            id: conversation.id,
            storedTitle: conversation.title,
            // A turn still being written counts as a question asked.
            questionCount:
              conversation.messages.filter((message) => message.role === "user")
                .length + (liveActiveTurn(conversation, now) === null ? 0 : 1),
            isShared: conversation.isShared,
          }),
        })),
        searched,
        onLoadMore: conversationsQuery.canFetchNextPage
          ? conversationsQuery.fetchNextPage
          : null,
      };
    }
    if (assistantQuery.state === "success")
      return {
        status: "success" as const,
        conversations: assistantQuery.docs.map((conversation) => ({
          id: conversation.id,
          title: assistantConversationListTitle(conversation),
          menu: conversationMenu({
            id: conversation.id,
            storedTitle: conversation.title,
            questionCount: conversation.questionCount,
            isShared: conversation.isShared,
          }),
        })),
        searched,
        onLoadMore: assistantQuery.canFetchNextPage
          ? assistantQuery.fetchNextPage
          : null,
      };
    throw new Error("should not happen: the engine's list is idle");
  })();

  const user = {
    name: [currentUser.firstname, currentUser.lastname]
      .filter((part) => part !== null)
      .join(" "),
    email: currentUser.email,
    specialty:
      currentUser.mainSpecialtyModel === null
        ? null
        : getItemTranslation(currentUser.mainSpecialtyModel),
    language: activeLang,
    picture: currentUser,
  };

  return (
    <DesktopSidebar
      state={state}
      width={width}
      onWidthChange={setWidth}
      onToggle={() => {
        setState(state === "expanded" ? "collapsed" : "expanded");
      }}
      activeDestination={activeDestinationOf(pathname)}
      session={{
        status: "ready",
        onNewConversation: () => {
          setNewChatGeneration((generation) => generation + 1);
        },
        activeConversationId,
        newContent: {
          explore: badges.Explore,
          rooms: badges.Rooms,
          // The mobile bar hangs this dot on JuisciGPT, the tab that reaches
          // the library there. Here the library has its own row.
          "visual-abstracts": badges.JuisciGPT,
        },
        search,
        onSearchChange: (text) => {
          setStoredSearch({ uid, text });
        },
        recents,
        user,
        onOpenSettings: () => {
          openDesktopSettings(ACCOUNT_PAGE);
        },
        onChangeLanguage: (lang) => {
          void changeLanguage(lang);
        },
        onLogOut: logOut,
      }}
      audioPlayer={
        <AppSidebarAudioPlayer
          sidebar={state}
          onExpandSidebar={() => {
            setState("expanded");
          }}
        />
      }
    />
  );
}
